import React, { useState, useEffect } from "react"; // Fixed Menu Fragment
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/rootReducer";
import menuButtonDark from "../../images/menu-button-dark.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faAdjust, faGlobe } from "@fortawesome/free-solid-svg-icons";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ISession, ILoggedUser } from "../../types/Auth";
import darkLogo from "../../images/softion-logo-dark.svg";
import { navigationResources } from "./.resources";
import { useResources } from "../../hooks/useResources";
import { LanguageOptions } from "../../types/Enums";
import Badge from "@mui/material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import * as urls from "../../types/Constants";

export interface IHeaderProps {
  session: ISession;
  themeName: "dark" | "light" | undefined;
  onMenuClick(): void;
  onSwitchLanguage(name: LanguageOptions): void;
  onSwitchTheme(name: ThemeOptions): void;
  onLogin(): void;
  onLogout(): void;
}

const Header: React.FC<IHeaderProps> = ({
  onMenuClick,
  session,
  themeName,
  onSwitchLanguage,
  onSwitchTheme,
  onLogin,
  onLogout,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const loggedUser: ILoggedUser = useSelector((state: RootState) => {
    return state.userInfo;
  });

  // Language sync is handled globally in App.tsx
  const res = useResources(navigationResources);
  const language: string = useSelector((state: RootState) => {
    return state.language;
  });

  const themeToString = (): string => {
    if (themeName === "dark") return res.switchToLightMode;
    return res.switchToDarkMode;
  };

  const [userError, setUserError] = useState<boolean>();
  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(
    null
  );
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    setUserError(false);
    if (loggedUser?.error) {
      setUserError(true);
    }
  }, [loggedUser]);

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountAnchor(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAccountAnchor(null);
  };

  const handleLanguageSelect = (lang: LanguageOptions) => {
    onSwitchLanguage(lang);
    handleLanguageClose();
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "#343a40",
        boxShadow: "none",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* Logo */}
        <Box
          component={NavLink}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            height: "92px",
          }}
        >
          <img
            src={darkLogo}
            className="logo"
            alt="Softion"
            style={{ height: "48px" }}
          />
        </Box>

        {/* Desktop Navigation Links */}
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <Button
              component={NavLink}
              to="/services"
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              {res.services}
            </Button>
            <Button
              component={NavLink}
              to="/products"
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              {res.products}
            </Button>
          </Box>
        )}

        {/* Right Side Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Desktop Actions */}
          {!isMobile && (
            <Box>
              {/* Theme Toggle */}
              <Tooltip title={themeToString()}>
                <IconButton
                  color="inherit"
                  onClick={() =>
                    onSwitchTheme(themeName === "dark" ? "light" : "dark")
                  }
                >
                  <FontAwesomeIcon icon={faAdjust} width="32px" height="32px" />
                </IconButton>
              </Tooltip>

              {/* Language Dropdown */}
              <Button
                color="inherit"
                onClick={handleLanguageClick}
                sx={{ textTransform: "none", minWidth: "auto", height: "32px" }}
              >
                <FontAwesomeIcon icon={faGlobe} style={{ marginRight: 8 }} />
                {language}
              </Button>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={handleLanguageClose}
              >
                <MenuItem onClick={() => handleLanguageSelect("en")}>
                  English
                </MenuItem>
                <MenuItem onClick={() => handleLanguageSelect("es")}>
                  Español
                </MenuItem>
              </Menu>

              {/* User Account or Login */}
              {session && session.loggedIn === true ? (
                <>
                  <Button
                    color="inherit"
                    onClick={handleAccountClick}
                    sx={{ textTransform: "none", minWidth: "auto" }}
                  >
                    <Badge
                      color="error"
                      badgeContent="!"
                      invisible={userError === false}
                    >
                      <PersonIcon
                        sx={{ marginRight: 1, height: "32px", width: "32px" }}
                      />
                    </Badge>
                    {session.user}
                  </Button>
                  <Menu
                    anchorEl={accountAnchor}
                    open={Boolean(accountAnchor)}
                    onClose={handleAccountClose}
                  >
                    <MenuItem
                      component={NavLink}
                      to="/my-account"
                      onClick={handleAccountClose}
                      disabled={userError}
                    >
                      {res.accountDetails}
                    </MenuItem>
                    <MenuItem
                      component={NavLink}
                      to="/my-account/my-purchases"
                      onClick={handleAccountClose}
                      disabled={userError}
                    >
                      {res.accountMyOrders}
                    </MenuItem>
                    <MenuItem
                      component={NavLink}
                      to="/my-account/my-products"
                      onClick={handleAccountClose}
                      disabled={userError}
                    >
                      {res.accountMyProducts}
                    </MenuItem>

                    {(loggedUser.isCustomer === true || loggedUser.isAdmin) && (
                      <MenuItem
                        component={NavLink}
                        to={urls.URL_MYLICENSES}
                        onClick={handleAccountClose}
                        disabled={userError}
                      >
                        {res.accountMyLicenses}
                      </MenuItem>
                    )}

                    {(loggedUser.isSupport === true ||
                      loggedUser.isAdmin === true) && (
                      <MenuItem
                        component={NavLink}
                        to={urls.URL_WIKI}
                        onClick={handleAccountClose}
                        disabled={userError}
                      >
                        Wiki
                      </MenuItem>
                    )}

                    {loggedUser.userId && (
                      <MenuItem
                        component={NavLink}
                        to={urls.URL_DOCUMENTS}
                        onClick={handleAccountClose}
                        disabled={userError}
                      >
                        Documentation
                      </MenuItem>
                    )}

                    {loggedUser.isAdmin === true && [
                      <Divider key="admin-divider-1" />,
                      <MenuItem
                        key="projects"
                        component={NavLink}
                        to="/admin/projects"
                        onClick={handleAccountClose}
                      >
                        Projects
                      </MenuItem>,
                      <MenuItem
                        key="products"
                        component={NavLink}
                        to="/admin/products"
                        onClick={handleAccountClose}
                      >
                        Products and Files
                      </MenuItem>,
                      <MenuItem
                        key="users"
                        component={NavLink}
                        to="/admin/users"
                        onClick={handleAccountClose}
                      >
                        {res.users}
                      </MenuItem>,
                      <MenuItem
                        key="files"
                        component={NavLink}
                        to="/admin/files"
                        onClick={handleAccountClose}
                      >
                        File Explorer
                      </MenuItem>,
                      <Divider key="admin-divider-2" />,
                      <MenuItem
                        key="diagnostics"
                        component={NavLink}
                        to="/diagnostics"
                        onClick={handleAccountClose}
                      >
                        Diagnostics
                      </MenuItem>,
                    ]}

                    <Divider />
                    <MenuItem
                      component={NavLink}
                      to="/logout"
                      onClick={() => {
                        handleAccountClose();
                        onLogout();
                      }}
                    >
                      {res.logout}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Tooltip title={res.login}>
                  <IconButton color="inherit" onClick={onLogin}>
                    <FontAwesomeIcon icon={faUser} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={onMenuClick}
              className="sidebar-menu-button"
            >
              <img
                alt="open"
                src={menuButtonDark}
                style={{ width: 32, height: 32, opacity: 0.8 }}
              />
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
